import { useTheme } from '@emotion/react'
import {
    isFeatureEnabled,
    toFeatureState,
} from '@etrigan/feature-toggles-client'
import {
    ConsentLevel,
    ExtendedAccessEvent,
    HeroVideo,
    ImageSourceWithCaptions,
    PlayerEvent,
    Product,
    useConsentGiven,
    useToggleState,
    VideoDetails,
} from '@news-mono/web-common'
import React from 'react'
import {
    AutoplayOptions,
    GetVideoQueue,
    VideoOptions,
} from '../../content/Video/PlayerInterface'
import { useGdprMode } from '../../content/Video/utils/gdpr-mode'
import {
    ArticleImage,
    FigcaptionRenderOptions,
} from '../../publication/ArticleImage/ArticleImage'
import { ArticleVideo } from '../../publication/ArticleVideo/ArticleVideo'
import { useProduct } from '../../__product/useProduct'

export interface ArticleHeroProps {
    heading: string
    heroImage?: ImageSourceWithCaptions
    heroVideo?: HeroVideo
    onEvent: (event: PlayerEvent | ExtendedAccessEvent) => void
    isVideoStickyEnabled?: boolean
    adUnitPath: string

    /**
     * Allows component to override defaults (including values defaulted by toggles)
     */
    autoplayOptionsOverrides?: false | Partial<AutoplayOptions>
    autoplayNextOptionssOverrides?: false | Partial<AutoplayOptions>

    showVideoTitles?: boolean
    getVideoQueue: GetVideoQueue
    publicationPathname?: string

    preferredCaptionSource?: 'image' | 'video'
    figcaptionRenderOptions?: FigcaptionRenderOptions
    imageRenderType?: 'feature'
    showVideoPlaylist?: boolean
}

export const ArticleHero = (props: ArticleHeroProps) => {
    const {
        showVideoTitles,
        heroVideo,
        heroImage,
        isVideoStickyEnabled,
        getVideoQueue,
        publicationPathname,
        adUnitPath,
        autoplayOptionsOverrides = {},
        autoplayNextOptionssOverrides = {},
        preferredCaptionSource = 'video',
        figcaptionRenderOptions,
        imageRenderType,
        showVideoPlaylist,
    } = props
    const theme = useTheme()
    const product = useProduct()
    const toggles = useToggleState()

    const gdprMode = useGdprMode()
    const consentGiven = useConsentGiven({
        consentRequiredLevel: ConsentLevel.Advertising | ConsentLevel.Analytics,
    })
    const canLoadVideoPlayer = gdprMode || consentGiven

    if (canLoadVideoPlayer && heroVideo) {
        const { videoId, heading } = heroVideo

        const videoDetails: VideoDetails = {
            id: videoId,
            accountId: heroVideo.accountId,
            heading,
            posterImage: heroVideo.posterImage,
            source: heroVideo.source,
            videoType: heroVideo.videoType,
        }

        const isAutoPlay = isFeatureEnabled(
            toFeatureState(toggles),
            'can-autoplay-video',
            true,
        )
        const isAutoPlayNext = isFeatureEnabled(
            toFeatureState(toggles),
            'can-autoplay-next',
        )
        const allowMutedAutoplay = isFeatureEnabled(
            toFeatureState(toggles),
            'can-autoplay-muted-video',
            true,
        )
        const isRegionalSite =
            product === Product.TheWest && theme.section !== 'default'

        const autoplayOptions: false | AutoplayOptions =
            autoplayOptionsOverrides !== false && isAutoPlay
                ? {
                      allowMuted: allowMutedAutoplay,
                      delayDuration: 3,
                      pauseCountdownOutsideViewport: true,
                      ...autoplayOptionsOverrides,
                  }
                : false
        const autoplayNextOptions: false | AutoplayOptions =
            isAutoPlayNext && autoplayNextOptionssOverrides !== false
                ? autoplayOptions
                    ? { ...autoplayOptions, ...autoplayNextOptionssOverrides }
                    : { delayDuration: 3, ...autoplayNextOptionssOverrides }
                : false

        const videoOptions: VideoOptions = {
            adUnitPath,
            canAutoplayVideos: false,
            enableSnowplow: isFeatureEnabled(
                toFeatureState(toggles),
                'snowplow',
            ),
            enablePrebid: isFeatureEnabled(
                toFeatureState(toggles),
                'prebid-video',
            ),
            enableNielsenVideoReporting:
                isFeatureEnabled(
                    toFeatureState(toggles),
                    'nielsen-video-events',
                ) && !isRegionalSite,

            enableNielsenSubbrandReporting: isFeatureEnabled(
                toFeatureState(toggles),
                'nielsen-subbrand',
            ),
            gdprMode,
        }

        const playlistToggleEnabled = isFeatureEnabled(
            toFeatureState(toggles),
            'video-playlist',
        )

        const canShowPlaylist =
            (playlistToggleEnabled && showVideoPlaylist) ||
            (playlistToggleEnabled && allowPlaylist(product))

        return (
            <ArticleVideo
                showTitles={Boolean(showVideoTitles)}
                videoDetails={videoDetails}
                autoplayOptions={autoplayOptions}
                autoplayNextOptions={autoplayNextOptions}
                videoOptions={videoOptions}
                captionText={getPreferredCaption(
                    preferredCaptionSource,
                    heroImage,
                    heroVideo,
                )}
                credit={
                    heroImage === undefined || product === 'sevennews'
                        ? getPreferredCredit(
                              preferredCaptionSource,
                              heroImage,
                              heroVideo,
                          )
                        : undefined
                }
                isHeroVideo={true}
                showPlaylist={canShowPlaylist}
                pauseOverlay
                getVideoQueue={getVideoQueue}
                isVideoStickyEnabled={isVideoStickyEnabled}
                onEvent={props.onEvent}
                publicationPathname={publicationPathname}
                figcaptionRenderOptions={figcaptionRenderOptions}
            />
        )
    }

    if (heroImage) {
        return (
            <ArticleImage
                image={heroImage}
                figCaption={{
                    byline: heroImage.byline,
                    captionText: heroImage.captionText,
                    credit: heroImage.credit,
                    kind: 'image',
                }}
                isHeroImage={true}
                figcaptionRenderOptions={figcaptionRenderOptions}
                imageRenderType={imageRenderType}
            />
        )
    }

    return (
        <ArticleImage
            image={theme.fallbackImages}
            isHeroImage={true}
            figcaptionRenderOptions={figcaptionRenderOptions}
            imageRenderType={imageRenderType}
        />
    )
}
ArticleHero.displayName = 'ArticleHero'

// 7NEWS would like unrelated videos to still appear with the caption of the main image
export function getPreferredCaption(
    preferredCaptionSource: 'image' | 'video',
    heroImage: ImageSourceWithCaptions | undefined,
    heroVideo: HeroVideo | undefined,
) {
    switch (preferredCaptionSource) {
        case 'video':
            return heroVideo
                ? heroVideo.captionText
                : heroImage
                ? heroImage.captionText
                : undefined

        default:
            return heroImage
                ? heroImage.captionText
                : heroVideo
                ? heroVideo.captionText
                : undefined
    }
}

// 7NEWS would like unrelated videos to still appear with the credit of the main image
export function getPreferredCredit(
    preferredCaptionSource: 'image' | 'video' | 'noCaption',
    heroImage: ImageSourceWithCaptions | undefined,
    heroVideo: HeroVideo | undefined,
) {
    if (preferredCaptionSource === 'noCaption') {
        console.log('no cap fired')

        return undefined
    }

    const heroVideoCredit =
        heroVideo && heroVideo.posterImage && heroVideo.posterImage.credit
            ? heroVideo.posterImage.credit
            : undefined
    const heroImageCredit =
        heroImage && heroImage.credit ? heroImage.credit : undefined

    if (preferredCaptionSource === 'video') {
        return heroVideoCredit ? heroVideoCredit : heroImageCredit
    }

    return heroImageCredit ? heroImageCredit : heroVideoCredit
}

function allowPlaylist(product: Product): boolean {
    switch (product) {
        case Product.PerthNow:
            return true
        case Product.SevenNews:
            return true
        case Product.TheWest:
            return false
        default:
            return false
    }
}
